import {
  Attachment,
  Case,
  CaseEventsPayload,
  CasePayload,
  CaseStatus,
  CasesPayload,
  ExternalUserRole,
  LawTypesPayload,
  Maybe,
  Note,
  OrderCaseBy,
  Task,
  UserCaseTasks,
  UsersPayload
} from '../../../generated'
import { SORT_DIR_TYPE } from '../../constants'
import { UserRoleOptionType } from '../../interfaceTypes'

export interface CaseState {
  casesData: CasePayload['case']
  isNewClientOpen: boolean
  isNewAdversaryOpen: boolean
  isNewJudgeOpen: boolean
  userCases: CasesPayload['cases']
  pageValue: number
  allCases: CasesPayload['cases']
  currentCase: Maybe<Case>
  allLawTypes: LawTypesPayload['lawTypes']
  allUsers: UsersPayload['users']
  attachmentData: Maybe<Array<Maybe<Attachment>>>
  currentAttachment: Maybe<Attachment>
  currentTask: Maybe<Task>
  isAddNewUserToTaskOpen: boolean
  isCaseTaskOpen: boolean
  isRefetch: boolean
  userCasesTask: Maybe<Maybe<UserCaseTasks>[]> | undefined
  caseEvents: CaseEventsPayload['caseEvents']
  allAssignedTasks: Maybe<Array<Maybe<Task>>>
  tabValue: number
  searchValue: string
  lawTypeId: string
  allExternalUserRoles: (ExternalUserRole | null)[]
  judgeRoleId: string
  clientRoleId: string
  adversaryRoleId: string
  caseStatus: string
  documentUrl: string
  isNoteDeleteOpen: boolean
  isNoteEditOpen: boolean
  isAssignedTaskEditOpen: boolean
  currentNote: Note | null
  caseFilter: string
  sortCasesByUser: UserRoleOptionType | undefined
  orderBy: OrderCaseBy
  orderByDirection: SORT_DIR_TYPE
  currentAssignedTask: Maybe<UserCaseTasks> | undefined
  contactRoleId: string
  clientId: string | undefined
  officeName: string | undefined
  billingType: string | undefined
  isWorkCompleted?: boolean
}

export const caseInitialState: CaseState = {
  casesData: null,
  isNewClientOpen: false,
  isNewAdversaryOpen: false,
  isNewJudgeOpen: false,
  userCases: [],
  pageValue: 0,
  allCases: [],
  currentCase: null,
  allLawTypes: undefined,
  allUsers: [],
  attachmentData: [],
  currentAttachment: null,
  currentTask: null,
  isAddNewUserToTaskOpen: false,
  isCaseTaskOpen: false,
  isRefetch: false,
  userCasesTask: undefined,
  caseEvents: undefined,
  allAssignedTasks: [],
  tabValue: 0,
  searchValue: '',
  lawTypeId: '',
  allExternalUserRoles: [],
  judgeRoleId: '',
  clientRoleId: '',
  adversaryRoleId: '',
  caseStatus: CaseStatus.Active,
  documentUrl: '',
  isNoteDeleteOpen: false,
  isNoteEditOpen: false,
  isAssignedTaskEditOpen: false,
  currentNote: null,
  caseFilter: 'active',
  sortCasesByUser: undefined,
  orderBy: OrderCaseBy.MatterNo,
  orderByDirection: 'DESC',
  currentAssignedTask: undefined,
  contactRoleId: '',
  clientId: '',
  officeName: undefined,
  billingType: undefined,
  isWorkCompleted: false
}

export enum CaseActionType {
  SET_CASES_DATA = 'setCasesData',
  SET_ALL_LAW_TYPES_DATA = 'setAllCaseTypesData',
  SET_NEW_CLIENT_OPEN = 'setNewClientOpen',
  SET_NEW_ADVERSARY_OPEN = 'setNewAdversaryOpen',
  SET_NEW_jUDGE_OPEN = 'setNewJudgeOpen',
  SET_USER_CASES = 'setUserCases',
  SET_PAGE_VALUE = 'setPageValue',
  SET_ALL_CASES = 'setAllCases',
  SET_CURRENT_CASE = 'setCurrentCase',
  SET_ALL_LAW_TYPES = 'setAllLawTypes',
  SET_ALL_EXTERNAL_USER_ROLES = 'setAllExternalUserRoles',
  SET_ALL_USERS = 'setAllUsers',
  SET_ATTACHMENT_DATA = 'setAttachmentData',
  SET_CURRENT_ATTACHMENT = 'setCurrentAttachment',
  SET_CURRENT_TASK = 'setCurrentTask',
  SET_IS_NEW_USER_TO_TASK_OPEN = 'setIsAddNewUserToTaskOpen',
  SET_IS_CASE_TASK_OPEN = 'setIsCaseTaskOpen',
  SET_IS_REFETCH = 'setIsRefetch',
  SET_USER_CASES_TASK = ' setUserCasesTask',
  SET_CASE_EVENTS = ' setCaseEvents',
  SET_ALL_ASSIGNED_TASKS = 'setAllAssignedTasks',
  SET_TAB_VALUE = 'setTabValue',
  SET_SEARCH_VALUE = 'setSearchValue',
  SET_LAW_TYPE_ID = 'setLawTypeId',
  SET_JUDGE_ROLE_ID = 'setJudgeRoleId',
  SET_CLIENT_ROLE_ID = 'setClientRoleId',
  SET_ADVERSARY_ROLE_ID = 'setAdversaryRoleId',
  SET_CASE_STATUS = 'setCaseStatus',
  SET_DOCUMENT_URL = 'setDocumentUrl',
  SET_IS_NOTE_DELETE_OPEN = 'setIsNoteDeleteOpen',
  SET_IS_ASSIGNED_TASK_EDIT_OPEN = 'setIssAssignedTaskEditOpen',
  SET_IS_NOTE_EDIT_OPEN = 'setIsNoteEditOpen',
  SET_IS_CURRENT_NOTE = 'setIsCurrentNote',
  SET_CASE_FILTER = 'setCaseFilter',
  SET_SORT_CASES_BY_USER = 'setSortCasesByUser',
  SET_CASE_ORDER_BY = 'setCaseOrderBy',
  SET_CURRENT_ASSIGNED_TASK = 'setCurrentAssignedTask',
  SET_CONTACT_ROLE_ID = 'setContactRoleId',
  SET_CASE_ORDER_DIRECTION = 'setCaseOrderDirection',
  SET_CLIENT_ID = 'setClientId',
  SET_OFFICE_NAME = 'setOfficeName',
  SET_BILLING_TYPE = 'setBillingType',
  SET_IS_WORK_COMPLETED = 'setIsWorkCompleted'
}

export type CaseAction =
  | { type: CaseActionType.SET_CASES_DATA; casesData: CasePayload['case'] }
  | { type: CaseActionType.SET_NEW_CLIENT_OPEN; isNewClientOpen: boolean }
  | { type: CaseActionType.SET_NEW_ADVERSARY_OPEN; isNewAdversaryOpen: boolean }
  | { type: CaseActionType.SET_NEW_jUDGE_OPEN; isNewJudgeOpen: boolean }
  | { type: CaseActionType.SET_USER_CASES; userCases: CasesPayload['cases'] }
  | { type: CaseActionType.SET_PAGE_VALUE; pageValue: number }
  | { type: CaseActionType.SET_ALL_CASES; allCases: CasesPayload['cases'] }
  | { type: CaseActionType.SET_CURRENT_CASE; currentCase: Maybe<Case> }
  | { type: CaseActionType.SET_ALL_LAW_TYPES; allLawTypes: LawTypesPayload['lawTypes'] }
  | { type: CaseActionType.SET_ALL_USERS; allUsers: UsersPayload['users'] }
  | { type: CaseActionType.SET_ATTACHMENT_DATA; attachmentData: Maybe<Array<Maybe<Attachment>>> }
  | { type: CaseActionType.SET_CURRENT_ATTACHMENT; currentAttachment: Maybe<Attachment> }
  | { type: CaseActionType.SET_CURRENT_TASK; currentTask: Maybe<Task> }
  | { type: CaseActionType.SET_IS_NEW_USER_TO_TASK_OPEN; isAddNewUserToTaskOpen: boolean }
  | { type: CaseActionType.SET_IS_CASE_TASK_OPEN; isCaseTaskOpen: boolean }
  | { type: CaseActionType.SET_IS_REFETCH; isRefetch: boolean }
  | { type: CaseActionType.SET_USER_CASES_TASK; userCasesTask: Maybe<Maybe<UserCaseTasks>[]> | undefined }
  | { type: CaseActionType.SET_CASE_EVENTS; caseEvents: CaseEventsPayload['caseEvents'] }
  | { type: CaseActionType.SET_ALL_ASSIGNED_TASKS; allAssignedTasks: Maybe<Array<Maybe<Task>>> }
  | { type: CaseActionType.SET_TAB_VALUE; tabValue: number }
  | { type: CaseActionType.SET_SEARCH_VALUE; searchValue: string }
  | { type: CaseActionType.SET_LAW_TYPE_ID; lawTypeId: string }
  | { type: CaseActionType.SET_ALL_EXTERNAL_USER_ROLES; allExternalUserRoles: (ExternalUserRole | null)[] }
  | { type: CaseActionType.SET_ADVERSARY_ROLE_ID; adversaryRoleId: string }
  | { type: CaseActionType.SET_CLIENT_ROLE_ID; clientRoleId: string }
  | { type: CaseActionType.SET_JUDGE_ROLE_ID; judgeRoleId: string }
  | { type: CaseActionType.SET_CASE_STATUS; caseStatus: string }
  | { type: CaseActionType.SET_DOCUMENT_URL; documentUrl: string }
  | { type: CaseActionType.SET_IS_NOTE_DELETE_OPEN; isNoteDeleteOpen: boolean }
  | { type: CaseActionType.SET_IS_NOTE_EDIT_OPEN; isNoteEditOpen: boolean }
  | { type: CaseActionType.SET_IS_CURRENT_NOTE; currentNote: Note | null }
  | { type: CaseActionType.SET_CASE_FILTER; caseFilter: string }
  | { type: CaseActionType.SET_SORT_CASES_BY_USER; sortCasesByUser: UserRoleOptionType | undefined }
  | { type: CaseActionType.SET_CASE_ORDER_BY; orderBy: OrderCaseBy }
  | { type: CaseActionType.SET_IS_ASSIGNED_TASK_EDIT_OPEN; isAssignedTaskEditOpen: boolean }
  | { type: CaseActionType.SET_CURRENT_ASSIGNED_TASK; currentAssignedTask: Maybe<UserCaseTasks> }
  | { type: CaseActionType.SET_CONTACT_ROLE_ID; contactRoleId: string }
  | { type: CaseActionType.SET_CASE_ORDER_DIRECTION; orderByDirection: SORT_DIR_TYPE }
  | { type: CaseActionType.SET_CLIENT_ID; clientId: string }
  | { type: CaseActionType.SET_OFFICE_NAME; officeName: string }
  | { type: CaseActionType.SET_BILLING_TYPE; billingType: string | undefined }
  | { type: CaseActionType.SET_IS_WORK_COMPLETED; isWorkCompleted: boolean | undefined }

export const caseReducer = (state: CaseState, action: CaseAction): CaseState => {
  switch (action.type) {
    case CaseActionType.SET_CASES_DATA:
      return {
        ...state,
        casesData: action.casesData
      }

    case CaseActionType.SET_NEW_CLIENT_OPEN:
      return {
        ...state,
        isNewClientOpen: action.isNewClientOpen
      }

    case CaseActionType.SET_PAGE_VALUE:
      return {
        ...state,
        pageValue: action.pageValue
      }

    case CaseActionType.SET_USER_CASES:
      return {
        ...state,
        userCases: action.userCases
      }

    case CaseActionType.SET_ALL_CASES:
      return {
        ...state,
        allCases: action.allCases
      }

    case CaseActionType.SET_ALL_EXTERNAL_USER_ROLES:
      return {
        ...state,
        allExternalUserRoles: action.allExternalUserRoles
      }

    case CaseActionType.SET_CURRENT_CASE:
      return {
        ...state,
        currentCase: action.currentCase
      }

    case CaseActionType.SET_ALL_LAW_TYPES:
      return {
        ...state,
        allLawTypes: action.allLawTypes
      }

    case CaseActionType.SET_ALL_USERS:
      return {
        ...state,
        allUsers: action.allUsers
      }

    case CaseActionType.SET_ATTACHMENT_DATA:
      return {
        ...state,
        attachmentData: action.attachmentData
      }

    case CaseActionType.SET_CURRENT_ATTACHMENT:
      return {
        ...state,
        currentAttachment: action.currentAttachment
      }

    case CaseActionType.SET_CURRENT_TASK:
      return {
        ...state,
        currentTask: action.currentTask
      }

    case CaseActionType.SET_IS_NEW_USER_TO_TASK_OPEN:
      return {
        ...state,
        isAddNewUserToTaskOpen: action.isAddNewUserToTaskOpen
      }

    case CaseActionType.SET_IS_CASE_TASK_OPEN:
      return {
        ...state,
        isCaseTaskOpen: action.isCaseTaskOpen
      }

    case CaseActionType.SET_IS_REFETCH:
      return {
        ...state,
        isRefetch: action.isRefetch
      }

    case CaseActionType.SET_USER_CASES_TASK:
      return {
        ...state,
        userCasesTask: action.userCasesTask
      }

    case CaseActionType.SET_CASE_EVENTS:
      return {
        ...state,
        caseEvents: action.caseEvents
      }

    case CaseActionType.SET_ALL_ASSIGNED_TASKS:
      return {
        ...state,
        allAssignedTasks: action.allAssignedTasks
      }

    case CaseActionType.SET_TAB_VALUE:
      return {
        ...state,
        tabValue: action.tabValue
      }

    case CaseActionType.SET_SEARCH_VALUE:
      return {
        ...state,
        searchValue: action.searchValue
      }

    case CaseActionType.SET_LAW_TYPE_ID:
      return {
        ...state,
        lawTypeId: action.lawTypeId
      }

    case CaseActionType.SET_NEW_ADVERSARY_OPEN:
      return {
        ...state,
        isNewAdversaryOpen: action.isNewAdversaryOpen
      }

    case CaseActionType.SET_NEW_jUDGE_OPEN:
      return {
        ...state,
        isNewJudgeOpen: action.isNewJudgeOpen
      }

    case CaseActionType.SET_ADVERSARY_ROLE_ID:
      return {
        ...state,
        adversaryRoleId: action.adversaryRoleId
      }

    case CaseActionType.SET_JUDGE_ROLE_ID:
      return {
        ...state,
        judgeRoleId: action.judgeRoleId
      }

    case CaseActionType.SET_CLIENT_ROLE_ID:
      return {
        ...state,
        clientRoleId: action.clientRoleId
      }

    case CaseActionType.SET_CASE_STATUS:
      return {
        ...state,
        caseStatus: action.caseStatus
      }

    case CaseActionType.SET_DOCUMENT_URL:
      return {
        ...state,
        documentUrl: action.documentUrl
      }

    case CaseActionType.SET_IS_NOTE_DELETE_OPEN:
      return {
        ...state,
        isNoteDeleteOpen: action.isNoteDeleteOpen
      }

    case CaseActionType.SET_IS_NOTE_EDIT_OPEN:
      return {
        ...state,
        isNoteEditOpen: action.isNoteEditOpen
      }

    case CaseActionType.SET_IS_CURRENT_NOTE:
      return {
        ...state,
        currentNote: action.currentNote
      }

    case CaseActionType.SET_CASE_FILTER:
      return {
        ...state,
        caseFilter: action.caseFilter
      }

    case CaseActionType.SET_SORT_CASES_BY_USER:
      return {
        ...state,
        sortCasesByUser: action.sortCasesByUser
      }

    case CaseActionType.SET_CASE_ORDER_BY:
      return {
        ...state,
        orderBy: action.orderBy
      }

    case CaseActionType.SET_IS_ASSIGNED_TASK_EDIT_OPEN:
      return {
        ...state,
        isAssignedTaskEditOpen: action.isAssignedTaskEditOpen
      }

    case CaseActionType.SET_CURRENT_ASSIGNED_TASK:
      return {
        ...state,
        currentAssignedTask: action.currentAssignedTask
      }
    case CaseActionType.SET_CONTACT_ROLE_ID:
      return {
        ...state,
        contactRoleId: action.contactRoleId
      }
    case CaseActionType.SET_CASE_ORDER_DIRECTION:
      return {
        ...state,
        orderByDirection: action.orderByDirection
      }
    case CaseActionType.SET_CLIENT_ID:
      return {
        ...state,
        clientId: action.clientId
      }
    case CaseActionType.SET_OFFICE_NAME:
      return {
        ...state,
        officeName: action.officeName
      }
    case CaseActionType.SET_BILLING_TYPE:
      return {
        ...state,
        billingType: action.billingType
      }
    case CaseActionType.SET_IS_WORK_COMPLETED:
        return {
          ...state,
          isWorkCompleted: action.isWorkCompleted
        }
    default:
      return state
  }
}
